import { useEffect, useState } from 'react'
import { ExitButton } from '../components/ExitButton'
import { api, type Garden as GardenData } from '../api'
import { radius, theme, TAP_MIN } from '../theme'
import { Button, Card, Heading, Page, Progress, inputStyle } from '../components/ui'
import { Empty, ErrorScreen, Loading } from '../components/States'
import { say } from '../lib/speech'
import { Trace, TraceRegion } from '../lib/trace'

type Sort = 'newest' | 'az' | 'hardest'

const SORTS: [Sort, string][] = [
  ['newest', 'Newest first'],
  ['az', 'A to Z'],
  ['hardest', 'Hardest won'],
]

/**
 * The garden: every word this child has met, and which ones have grown.
 *
 * This is the only screen that shows three years of work at once, and it is
 * the reason the old data was worth importing. A number that only goes up is
 * a better reward than any badge.
 *
 * "Grown" means five clean recalls AND a review gap of at least a fortnight.
 * Streak alone would let five right answers inside one session count as
 * knowing a word.
 */
export function Garden({
  learnerId, onHome, onBadges, onWordMap,
}: {
  learnerId: string; onHome: () => void; onBadges: () => void
  onWordMap: () => void
}) {
  const [data, setData] = useState<GardenData | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<Sort>('newest')

  const load = () => {
    setError(null)
    api.garden(learnerId, search, sort).then(setData).catch(setError)
  }
  useEffect(load, [learnerId, sort])

  if (error) return <ErrorScreen error={error} onRetry={load} onHome={onHome} />
  if (!data) return <Loading />

  return (
    <Page width={620}>
      <Heading sub={
        <>
          <Trace id="calc.rows in T7 learner_word that have been seen at least once">
            {data.words_met.toLocaleString()} words met
          </Trace>
          {' · '}
          <Trace id="calc.words at the floor in all three T8 vectors, gap of a fortnight or more"
                 title="a bar that only rises: this number cannot fall (D-048)">
            {data.words_mastered} fully grown
          </Trace>
        </>
      }>
        <Trace id="T2.display_name">{data.display_name}</Trace>&apos;s garden
      </Heading>

      {data.empty_message ? (
        <Empty emoji="🌱" title="Nothing growing yet" line={data.empty_message} />
      ) : (
        <>
          {data.newest_sprout ? (
            <Card style={{ background: theme.greenPale, borderColor: theme.green, marginBottom: 20 }}>
              <div style={{
                fontSize: 12, fontWeight: 800, letterSpacing: 1,
                color: theme.greenDark, marginBottom: 6,
              }}>
                NEWEST SPROUT
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 26, fontWeight: 800, color: theme.greenDarker }}>
                  <Trace id="T3.headword">{data.newest_sprout.word}</Trace>
                </span>
                <button
                  type="button"
                  onClick={() => say(data.newest_sprout!.word)}
                  style={{
                    fontFamily: 'inherit', fontSize: 14, color: theme.greenDark,
                    background: '#FFFFFF', border: `1px solid ${theme.green}`,
                    borderRadius: radius.sm, padding: '6px 10px', cursor: 'pointer',
                  }}
                >
                  🔊
                </button>
              </div>
              {data.newest_sprout.definition ? (
                <p style={{ margin: '8px 0 0', fontSize: 16, color: theme.greenDarker, lineHeight: 1.5 }}>
                  <Trace id="T4.definition">{data.newest_sprout.definition}</Trace>
                </p>
              ) : null}
              {data.newest_sprout.example_sentence ? (
                <p style={{
                  margin: '8px 0 0', fontSize: 15, color: theme.greenDark,
                  lineHeight: 1.55, fontStyle: 'italic',
                }}>
                  {data.newest_sprout.example_sentence}
                </p>
              ) : null}
            </Card>
          ) : null}

          {data.shelves.length ? (
            <>
              <h2 style={{ fontSize: 18, fontWeight: 800, color: theme.text, margin: '0 0 12px' }}>
                Your shelves
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
                {data.shelves.map((shelf) => (
                  <Card key={shelf.word_list_id} style={{ padding: 16 }}>
                    <div style={{
                      display: 'flex', justifyContent: 'space-between',
                      gap: 10, marginBottom: 8,
                    }}>
                      <span style={{ fontSize: 16, fontWeight: 700, color: theme.text }}>
                        {shelf.name}
                      </span>
                      <span style={{ fontSize: 14, color: theme.faint, whiteSpace: 'nowrap' }}>
                        {shelf.grown ?? 0} / {shelf.total}
                      </span>
                    </div>
                    <Progress done={shelf.grown ?? 0} total={shelf.total} />
                  </Card>
                ))}
              </div>
            </>
          ) : null}

          <h2 style={{ fontSize: 18, fontWeight: 800, color: theme.text, margin: '0 0 12px' }}>
            Find a word
          </h2>

          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <input
              style={{ ...inputStyle, flex: 1 }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') load() }}
              placeholder="Type any word"
            />
            <Button tone="quiet" onClick={load}>Find</Button>
          </div>

          <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
            {SORTS.map(([key, label]) => {
              const on = sort === key
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSort(key)}
                  style={{
                    fontFamily: 'inherit', fontSize: 14, fontWeight: on ? 700 : 600,
                    color: on ? theme.orangeDark : theme.muted,
                    background: on ? theme.orangePale : 'transparent',
                    border: `1px solid ${on ? theme.orange : theme.border}`,
                    borderRadius: radius.sm, padding: '7px 12px',
                    minHeight: TAP_MIN, cursor: 'pointer',
                  }}
                >
                  {label}
                </button>
              )
            })}
          </div>

          {data.words.length === 0 ? (
            <Empty emoji="🔍" title="No word like that" line="Try a different spelling." />
          ) : (
            <TraceRegion id="T7"
                         note="joined to T3 word; the leaf is worked out from T8">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {data.words.map((w) => (
                <span
                  key={w.word}
                  title={w.definition ?? undefined}
                  style={{
                    fontSize: 15, fontWeight: w.grown ? 700 : 600,
                    color: w.grown ? theme.greenDarker : theme.text2,
                    background: w.grown ? theme.greenPale : '#FFFFFF',
                    border: `1.5px solid ${w.grown ? theme.green : theme.border}`,
                    borderRadius: radius.pill, padding: '7px 13px',
                  }}
                >
                  {w.grown ? '🌿 ' : ''}{w.word}
                </span>
              ))}
            </div>
            </TraceRegion>
          )}
        </>
      )}

      <div style={{ display: 'flex', gap: 10, marginTop: 26, flexWrap: 'wrap' }}>
        <Button tone="child" onClick={onWordMap}>🗺️ Word map</Button>
        <Button tone="quiet" onClick={onBadges}>Badges</Button>
        <ExitButton onExit={onHome} />
      </div>
    </Page>
  )
}
